import { Box } from '@mui/system';
import { useStacklineTheme } from '@stackline/ui';
import React, { useMemo } from 'react';
import { ValueOf } from 'sl-api-connector';
import NewDorisMetricCard from 'src/components/AtlasV2Components/AtlasKeyMetricsV2PageComponents/NewDorisMetricCard';
import {
  DorisFieldDisplayNameEnum,
  DorisMetricEndPointEnum,
  DorisMetricTypeEnum
} from 'src/components/AtlasV2Components/Consts/DorisFieldNameEnum';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { EnhancedCommonSummaryInfoWithLegend } from 'src/components/EntityPage/CommonSummaryInfo/CommonSummaryInfo';
import { KEY_METRICS_CONTAINER_ADJUSTED_WIDTH } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { updateUrlQueryParams } from 'src/utils/app';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { buildSubtitleDisplayName } from 'src/utils/filters';
import { useHistory } from 'src/utils/Hooks';

interface BasicDorisKeyMetricCardDefinition {
  /** React list key. Unique per card - several cards share a metric type. */
  key: string;
  apiMetricType: string;
  title: string;
  variant: 'large' | 'small';
  metricTypeToDisplay: ValueOf<typeof METRICTYPE>;
  onClick: () => void;
  showMarketShare?: boolean;
  showAverageRate?: boolean;
  showLastweek?: boolean;
}

/**
 * Key metric summary cards for the BASIC Atlas scorecard - retailers or clients without traffic
 * data (e.g. TikTok Shop). Same twelve cards and layout as the Elasticsearch version in
 * AtlasV2BasicKeyMetricsCards, but sourced from Doris.
 */
const AtlasV2BasicKeyMetricsCardsV2 = () => {
  const theme = useStacklineTheme();
  const history = useHistory();

  const cardDefinitions: BasicDorisKeyMetricCardDefinition[][] = useMemo(
    () => [
      // Column 1 - Retail sales
      [
        {
          key: 'retailSales',
          apiMetricType: DorisMetricTypeEnum.RetailSales,
          title: DorisFieldDisplayNameEnum.RetailSales,
          variant: 'large',
          metricTypeToDisplay: METRICTYPE.MONEY,
          onClick: () => history.push(updateUrlQueryParams({ tab: 'sales', subtab: 'retailSales' }))
        },
        {
          key: 'retailSalesMarketShare',
          apiMetricType: DorisMetricTypeEnum.RetailSales,
          title: DorisFieldDisplayNameEnum.RetailSalesMarketShareV2,
          variant: 'small',
          metricTypeToDisplay: METRICTYPE.PERCENT,
          showMarketShare: true,
          onClick: () => history.push(updateUrlQueryParams({ tab: 'sales', subtab: 'retailSales' }))
        },
        {
          key: 'retailSalesAvg',
          apiMetricType: DorisMetricTypeEnum.RetailSalesAvg,
          title: DorisFieldDisplayNameEnum.RetailSalesAvg,
          variant: 'small',
          metricTypeToDisplay: METRICTYPE.MONEY,
          showAverageRate: true,
          onClick: () => history.push(updateUrlQueryParams({ tab: 'sales', subtab: 'retailSales' }))
        },
        {
          key: 'promotionCount',
          apiMetricType: DorisMetricTypeEnum.PromotionCount,
          title: DorisFieldDisplayNameEnum.PromotionCount,
          variant: 'small',
          metricTypeToDisplay: METRICTYPE.VOLUME,
          onClick: () => history.push(updateUrlQueryParams({ tab: 'prices', subtab: 'promotions' }))
        }
      ],
      // Column 2 - Units sold
      [
        {
          key: 'unitsSold',
          apiMetricType: DorisMetricTypeEnum.UnitsSold,
          title: DorisFieldDisplayNameEnum.UnitsSold,
          variant: 'large',
          metricTypeToDisplay: METRICTYPE.VOLUME,
          onClick: () => history.push(updateUrlQueryParams({ tab: 'sales', subtab: 'unitsSold' }))
        },
        {
          key: 'unitsSoldMarketShare',
          apiMetricType: DorisMetricTypeEnum.UnitsSold,
          title: DorisFieldDisplayNameEnum.UnitsSoldMarketShareV2,
          variant: 'small',
          metricTypeToDisplay: METRICTYPE.PERCENT,
          showMarketShare: true,
          onClick: () => history.push(updateUrlQueryParams({ tab: 'sales', subtab: 'unitsSold' }))
        },
        {
          key: 'unitsSoldAvg',
          apiMetricType: DorisMetricTypeEnum.UnitsSoldAvg,
          title: DorisFieldDisplayNameEnum.UnitsSoldAvg,
          variant: 'small',
          metricTypeToDisplay: METRICTYPE.VOLUME,
          showAverageRate: true,
          onClick: () => history.push(updateUrlQueryParams({ tab: 'sales', subtab: 'unitsSold' }))
        },
        {
          key: 'productsWithSalesCount',
          apiMetricType: DorisMetricTypeEnum.ProductsWithSalesCount,
          title: DorisFieldDisplayNameEnum.StacklineSkuCount,
          variant: 'small',
          metricTypeToDisplay: METRICTYPE.VOLUME,
          showLastweek: true,
          onClick: () => history.push(updateUrlQueryParams({ tab: 'sales', subtab: 'retailSales' }))
        }
      ],
      // Column 3 - Price, content and rating
      [
        {
          key: 'retailPrice',
          apiMetricType: DorisMetricTypeEnum.RetailPrice,
          title: DorisFieldDisplayNameEnum.RetailPrice,
          variant: 'large',
          metricTypeToDisplay: METRICTYPE.MONEY,
          showAverageRate: true,
          onClick: () => history.push(updateUrlQueryParams({ tab: 'prices', subtab: 'retailPrice' }))
        },
        {
          key: 'retailPriceMarketShare',
          apiMetricType: DorisMetricTypeEnum.RetailPrice,
          title: DorisFieldDisplayNameEnum.RetailPriceMarketShareV2,
          variant: 'small',
          metricTypeToDisplay: METRICTYPE.PERCENT,
          showMarketShare: true,
          onClick: () => history.push(updateUrlQueryParams({ tab: 'prices', subtab: 'retailPrice' }))
        },
        {
          key: 'averageContentScore',
          apiMetricType: DorisMetricTypeEnum.AverageContentScore,
          title: DorisFieldDisplayNameEnum.ContentScore,
          variant: 'small',
          metricTypeToDisplay: METRICTYPE.PERCENT,
          showLastweek: true,
          onClick: () => history.push(updateUrlQueryParams({ tab: 'content', subtab: 'contentScore' }))
        },
        {
          key: 'reviewsRating',
          apiMetricType: DorisMetricTypeEnum.ReviewsRating,
          title: DorisFieldDisplayNameEnum.ReviewsRating,
          variant: 'small',
          metricTypeToDisplay: METRICTYPE.DECIMAL,
          showLastweek: true,
          onClick: () => history.push(updateUrlQueryParams({ tab: 'rating', subtab: 'rating' }))
        }
      ]
    ],
    [history]
  );

  return (
    <>
      <EnhancedCommonSummaryInfoWithLegend
        widget={{
          view: {
            buildSubTitle: ({ retailer, mainEntity, filters, categories, app }) =>
              mainEntity ? buildSubtitleDisplayName(retailer, mainEntity, filters, categories, app) : 'Summary',

            title: 'Key Metrics'
          }
        }}
      />

      <Box
        marginTop={theme.spacing.lg}
        marginBottom={theme.spacing.lg}
        sx={{
          transform: 'translateX(-16px)',
          width: KEY_METRICS_CONTAINER_ADJUSTED_WIDTH
        }}
      >
        <Flex gap={24}>
          {cardDefinitions.map((column, index) => (
            <Flex flexDirection="column" key={`column-${index}`} gap={16}>
              {column.map((card) => (
                <NewDorisMetricCard
                  key={card.key}
                  variant={card.variant}
                  // Keyed by metric type, not card key, so two cards on the same metric
                  // (e.g. Retail Sales and Share ($)) share one react-query entry and
                  // issue a single request when their payloads match.
                  queryKey={card.apiMetricType}
                  apiMetricType={card.apiMetricType}
                  fieldName={card.apiMetricType}
                  methodName={DorisMetricEndPointEnum.SalesEndPoint}
                  metricTypeToDisplay={card.metricTypeToDisplay}
                  title={card.title}
                  onClick={card.onClick}
                  showMarketShare={card.showMarketShare}
                  showAverageRate={card.showAverageRate}
                  showLastweek={card.showLastweek}
                />
              ))}
            </Flex>
          ))}
        </Flex>
      </Box>
    </>
  );
};

export default AtlasV2BasicKeyMetricsCardsV2;
